import { formatPersonName } from '@/utils/personName';
/**
 * BusinessPersonDialog — Business-group entry point for the RAO people form.
 *
 * Mirrors `IndividualPersonDialog` but for the 9 Business sub-types (Corp /
 * LLC / DBA / Partnership / NonProfit / UnincorpAssoc / IOLTA / Political /
 * ICS_Business per business.md §1).
 *
 * Section order (top → bottom):
 *   1. BusinessIdentitySection         — sub-type picker + legal name + EIN + NAICS
 *   2. BusinessCipSection (SoS)        — state registration metadata + Lookup button
 *   3. AddressSection (shared)         — physical + mailing
 *   4. ContactSection (shared)         — phones + email
 *   5. DocumentSlotsSection (shared)   — per-sub-type required document slots
 *
 * Omitted vs Individual:
 *   - No CIP ID scan panel (TWAIN/camera/upload) — business entities don't
 *     present a personal photo ID. Authorized signers still need their own
 *     Individual CIP dialogs (separate person rows on the cart).
 *   - No Citizenship section — businesses aren't citizens.
 *   - No Employment matrix — replaced by NAICS + Business Industry fields
 *     inside BusinessIdentitySection.
 *
 * Supports:
 *   - `readOnly` (locks all inputs + hides save controls)
 *   - always-modal surface (the inline 'existing-cif-maint' mode was retired 2026-04-24)
 *   - `attachedDocs` lifting (same contract as Individual — CIP upload + any
 *     document registrations flow into a local store so DocumentSlotsSection
 *     sees fulfillment state)
 *
 * Dispatcher wiring deliberately NOT done here — the parent `PersonDialog.tsx`
 * is edited separately to avoid write conflicts with the other builder agents.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ModalShell } from '@/components/primitives';
import type { RaoPerson } from '../../../types';
import { BusinessIdentitySection } from './BusinessIdentitySection';
import { BusinessCipSection } from './BusinessCipSection';
import { BeneficialOwnerWorksheet } from './BeneficialOwnerWorksheet';
import { boRequirementFor } from './beneficialOwners';
import { useBusinessDerivation } from './useBusinessDerivation';

// Shared sections from personDialog/shared/
import { AddressSection } from '../shared/AddressSection';
import { ContactSection } from '../shared/ContactSection';
import { DocumentSlotsSection } from '../shared/DocumentSlotsSection';
import { PdwFooterDyad } from '../shared/PdwFooterDyad';
import {
  type AttachedDoc,
  readAttachedDocs,
  mergeAttachedDoc,
  mergeAttachedDocsKeepExisting,
} from '../shared/attachedDocs';
import { useAttachedDocsHydration } from '../shared/useInquiryHydration';
import { EntityConvergedShell } from '../EntityConvergedShell';
import type { PdwTabKey } from '../PdwTabStrip';

// -----------------------------------------------------------------------------
// Props
// -----------------------------------------------------------------------------

/**
 * Dialog surface modes — same taxonomy as IndividualPersonDialog. Modal
 * experience in all three cases; Maint CIF opens with `mode="edit"`.
 */
export type BusinessPersonDialogMode = 'review-add' | 'new' | 'edit';

export interface BusinessPersonDialogProps {
  open: boolean;
  mode: BusinessPersonDialogMode;
  person: RaoPerson;
  onSave: (person: RaoPerson) => void;
  onClose: () => void;
  cartCifIds?: ReadonlyArray<string>;
  /** Required by DocumentSlotsSection for per-cart document attachment. */
  cartId: string;
  /** Required by DocumentSlotsSection for upload metadata. Defaults 'UNKNOWN'. */
  officerCode?: string;
  /** When true, disables every input + hides edit/save controls. */
  readOnly?: boolean;
  /** Early Core search (PeopleStep) — the "No EIN / TIN" proof searches by the entity name. */
  onEarlyDupCheck?: (draft: RaoPerson, markNoTinCleared?: (key: string) => void) => void;
}

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export function BusinessPersonDialog({
  open,
  mode,
  person,
  onSave,
  onClose,
  cartCifIds,
  cartId,
  officerCode,
  readOnly = false,
  onEarlyDupCheck,
}: BusinessPersonDialogProps): JSX.Element | null {
  const [local, setLocal] = useState<RaoPerson>(person);

  // "No EIN / TIN" proof (Bill 2026-10-14): once the box is ticked and the entity name is in, search
  // Core by that name; no match stamps noTinClearedKey (the Review gate then waives the tax ID).
  const lastEntityCheckRef = useRef<string>('');
  useEffect(() => {
    if (!onEarlyDupCheck || readOnly || mode !== 'new' || local.cifId || local.tinCode !== 'N') return;
    const name = (local.name2 ?? '').trim().toLowerCase();
    if (!name || lastEntityCheckRef.current === name) return;
    lastEntityCheckRef.current = name;
    onEarlyDupCheck(local, (cleared) => setLocal((l) => ({ ...l, noTinClearedKey: cleared })));
  }, [local, onEarlyDupCheck, readOnly, mode]);
  const [hasChanges, setHasChanges] = useState<boolean>(false);

  // Slot-4 T0 (Contract C2) — attachedDocs live on the REAL draft
  // (local.extra.attachedDocs), not a sibling useState that drops on save.
  const attachedDocs = useMemo<ReadonlyArray<AttachedDoc>>(() => readAttachedDocs(local), [local]);

  const registerAttachedDoc = useCallback((doc: AttachedDoc): void => {
    setLocal((prev) => mergeAttachedDoc(prev, doc));
    setHasChanges(true);
  }, []);

  useEffect(() => {
    setLocal(person);
    setHasChanges(false);
  }, [person]);

  // Slot-4 T5 — Edit-mode doc hydration from the v3 Document Library.
  const { fetchAttachedDocsForCif } = useAttachedDocsHydration();
  const hydratedCifRef = useRef<string | null>(null);
  useEffect(() => {
    const cifId = (person.cifId ?? '').trim();
    if (mode === 'new' || !cifId || hydratedCifRef.current === cifId) return;
    hydratedCifRef.current = cifId;
    let cancelled = false;
    void (async () => {
      const docs = await fetchAttachedDocsForCif(cifId);
      if (cancelled || docs.length === 0) return;
      setLocal((prev) => mergeAttachedDocsKeepExisting(prev, docs));
    })();
    return () => {
      cancelled = true;
    };
  }, [person.cifId, mode, fetchAttachedDocsForCif]);

  const sectionMode: 'new' | 'existing' = mode === 'new' ? 'new' : 'existing';
  const derivation = useBusinessDerivation(local, {
    mode: sectionMode,
    storedType: person.customerType,
  });

  const update = useCallback((patch: Partial<RaoPerson>): void => {
    setLocal((prev) => {
      const next: RaoPerson = { ...prev, ...patch };
      // Keep fullName in sync for downstream cart rendering — business
      // display name is the legal entity name (first+last joined).
      const first = next.firstName ?? '';
      const last = next.lastName ?? '';
      const display = [first, last].filter((x) => !!x).join(' ').trim();
      if (display) next.fullName = display;
      else if (next.name2) next.fullName = next.name2;
      // Mark existing CIFs dirty so commit pipeline branches to CustMod.
      if (prev.mode === 'existing') next.dirty = true;
      return next;
    });
    setHasChanges(true);
  }, []);

  const isChanged = useCallback(
    (field: string): boolean => {
      const a = (local as unknown as Record<string, unknown>)[field];
      const b = (person as unknown as Record<string, unknown>)[field];
      return a !== b && !(a == null && b == null);
    },
    [local, person],
  );

  const displayName =
    local.firstName || local.lastName
      ? formatPersonName(local)
      : local.name2 ?? '';

  const title =
    mode === 'review-add'
      ? `Review & add — ${displayName}`
      : mode === 'new'
        ? 'Add new business entity'
        : `Edit business — ${displayName}`;

  const btnLabel =
    mode === 'review-add'
      ? hasChanges ? 'Add existing w/ changes' : 'Add existing'
      : mode === 'new'
        ? 'Add new'
        : 'Save changes';

  // Save-disable surfaces: duplicate CIF already on cart + supervisor gate.
  const dupInCart =
    !!(local.cifId && cartCifIds?.includes(local.cifId)) && mode === 'review-add';
  const saveDisabled = dupInCart || !!derivation.requiresSupervisorApproval;

  // BACKLOG-0016 — sticky-footer Return/Continue dyad. Business dialog is
  // flat-scroll (single stage), so Return is disabled and Continue carries
  // the save action with the mode-aware btnLabel.
  const continueDisabledReason = dupInCart
    ? `CIF ${local.cifId ?? ''} is already on this cart.`
    : derivation.requiresSupervisorApproval
      ? 'Supervisor approval required before saving.'
      : undefined;

  // ---------------------------------------------------------------------------
  // Sections grouped by canonical tab (D3 convergence). Business surfaces:
  // Profile (sub-type + legal name + EIN + NAICS) · CIP (SoS registration) ·
  // Contact · Documents. No personal photo-ID CIP (signers carry their own).
  // ---------------------------------------------------------------------------
  const sectionsByTab: Partial<Record<PdwTabKey, JSX.Element>> = {
    profile: (
      <BusinessIdentitySection
        person={local}
        onChange={update}
        mode={sectionMode}
        isChanged={isChanged}
        derivation={derivation}
        readOnly={readOnly}
      />
    ),
    cip: (
      <BusinessCipSection
        person={local}
        onChange={update}
        mode={sectionMode}
        isChanged={isChanged}
        readOnly={readOnly}
      />
    ),
    ownership: (
      <BeneficialOwnerWorksheet
        person={local}
        onChange={update}
        isChanged={isChanged}
        readOnly={readOnly}
      />
    ),
    contact: (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <AddressSection person={local} onChange={update} isChanged={isChanged} readOnly={readOnly} />
        <ContactSection person={local} onChange={update} isChanged={isChanged} readOnly={readOnly} />
      </div>
    ),
    documents: (
      <DocumentSlotsSection
        person={local}
        cartId={cartId}
        officerCode={officerCode ?? 'UNKNOWN'}
        entityType={String(derivation.customerType)}
        attachedDocs={attachedDocs}
        onDocumentRegistered={registerAttachedDoc}
        readOnly={readOnly}
      />
    ),
  };

  // Modal surface — every entry into the dialog (New Acct + Maint CIF).
  // Dyad rendered as a final child so its sticky-bottom + negative margins
  // extend to the modal's outer edges (BACKLOG-0016).
  return (
    <ModalShell open={open} onClose={onClose} title={title} size="lg">
      <EntityConvergedShell
        person={local}
        mode={mode}
        dupInCart={dupInCart}
        tabs={
          boRequirementFor(local.customerType).kind !== 'none'
            ? ['review', 'profile', 'cip', 'ownership', 'contact', 'documents']
            : ['review', 'profile', 'cip', 'contact', 'documents']
        }
        sectionsByTab={sectionsByTab}
        onAttachedDocsChange={(docs) => docs.forEach(registerAttachedDoc)}
        onPersonPatch={update}
        cartId={cartId}
        officerCode={officerCode ?? 'UNKNOWN'}
        readOnly={readOnly}
      />

      <PdwFooterDyad
        currentStep={0}
        totalSteps={1}
        onReturn={onClose}
        onContinue={() => onSave(local)}
        continueDisabled={saveDisabled || readOnly}
        continueDisabledReason={continueDisabledReason}
        lastStepLabel={`${btnLabel} →`}
      />
    </ModalShell>
  );
}
